import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { CreatableAircraftFile } from "@nova/schemas"
import { useSWRNoRevalidate } from "@nova/swr"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { mutate } from "swr"
import useSWRMutation from "swr/mutation"

export const invalidateAircraftFiles = () =>
    mutate((key) => Array.isArray(key) && key[0] === "get-aircraft-files")

const getAircraftFiles = (orgId: string, aircraftId: string) => {
    return fetchApiGateway("get /org/:orgId/fleet/aircraft/:aircraftId/file", {
        params: { orgId, aircraftId },
    })
}

export const useAircraftFiles = (aircraftId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading, mutate } = useSWRNoRevalidate(
        orgId ? ["get-aircraft-files", orgId, aircraftId] : null,
        () => (orgId ? getAircraftFiles(orgId, aircraftId) : undefined)
    )

    return { data, isLoading, mutate }
}

const createAircraftFile = (orgId: string, aircraftId: string, body: CreatableAircraftFile) => {
    return fetchApiGateway("post /org/:orgId/fleet/aircraft/:aircraftId/file", {
        body,
        params: { orgId, aircraftId },
    })
}

export const useCreateAircraftFile = (aircraftId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-aircraft-file",
        (key, { arg }: { arg: CreatableAircraftFile }) =>
            orgId ? createAircraftFile(orgId, aircraftId, arg) : undefined,
        { onSuccess: invalidateAircraftFiles }
    )

    return {
        createAircraftFile: trigger,
        isCreating: isMutating,
    }
}

const deleteAircraftFile = (orgId: string, aircraftId: string, fileId: string) => {
    return fetchApiGateway("delete /org/:orgId/fleet/aircraft/:aircraftId/file/:fileId", {
        params: { orgId, aircraftId, fileId },
    })
}

export const useDeleteAircraftFile = (aircraftId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-aircraft-file",
        (key, { arg }: { arg: string }) =>
            orgId ? deleteAircraftFile(orgId, aircraftId, arg) : undefined,
        { onSuccess: invalidateAircraftFiles }
    )

    return {
        deleteAircraftFile: trigger,
        isDeleting: isMutating,
    }
}

export const aircraftFileS3Key = (orgId: string, aircraftId: string, fileId: string) => {
    return `public/org/${orgId}/assets/aircraft/${aircraftId}/files/${fileId}`
}
